<script setup>
import { computed, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import {
  NODE_TYPES,
  DELAY_UNITS,
  ACTION_TYPES,
  CHAT_MESSAGE_TYPES,
  CONDITION_TYPES,
  CONVERSATION_STATUSES,
  PRIORITIES,
  HTTP_METHODS,
  PLACEHOLDERS,
} from '../constants';
import { uid } from '../graph';
import { useStore } from 'dashboard/composables/store';
import VarInput from './VarInput.vue';

const props = defineProps({
  block: { type: Object, required: true },
});

const emit = defineEmits(['close', 'change', 'add-condition']);

const store = useStore();

const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const allLabels = useMapGetter('labels/getLabels');
const pipelines = useMapGetter('pipelines/getPipelines');

store.dispatch('pipelines/get');

const allStages = computed(() => {
  const list = [];
  (pipelines.value || []).forEach(pipeline => {
    (pipeline.pipeline_stages || []).forEach(stage => {
      list.push({ id: stage.id, name: `${pipeline.name} > ${stage.name}`, pipelineId: pipeline.id });
    });
  });
  return list;
});

const labelSearch = ref('');

const definition = computed(() => NODE_TYPES[props.block.type] || {});
const options = computed(() => props.block.options);

const touch = () => emit('change');

if (props.block.type === 'chat') {
  const messages = options.value.messages || (options.value.messages = []);
  if (!messages.some(m => m.name === 'send-text-message')) {
    messages.unshift({
      name: 'send-text-message',
      options: { text: '', buttons: [] },
    });
  }
}
if (props.block.type === 'api' && !options.value.apis?.length) {
  options.value.apis = NODE_TYPES.api.defaults().apis;
}

const ACCENT = {
  blue: { bg: 'rgba(var(--blue-4), 0.4)', text: 'rgb(var(--blue-11))' },
  amber: { bg: 'rgba(var(--amber-4), 0.4)', text: 'rgb(var(--amber-11))' },
  teal: { bg: 'rgba(var(--teal-4), 0.4)', text: 'rgb(var(--teal-11))' },
  violet: { bg: 'rgba(var(--violet-4), 0.4)', text: 'rgb(var(--violet-11))' },
  iris: { bg: 'rgba(var(--iris-4), 0.4)', text: 'rgb(var(--iris-11))' },
  ruby: { bg: 'rgba(var(--ruby-4), 0.4)', text: 'rgb(var(--ruby-11))' },
  slate: { bg: 'rgba(var(--slate-4), 0.4)', text: 'rgb(var(--slate-11))' },
};
const accentColors = computed(
  () => ACCENT[definition.value.accent] || ACCENT.slate
);

/* ---------------------------------------------------------------- trigger */
const keywordsText = computed({
  get: () => (options.value.keywords || []).join(', '),
  set: value => {
    options.value.keywords = value
      .split(',')
      .map(word => word.trim())
      .filter(Boolean);
    touch();
  },
});

const triggerLabelsText = computed({
  get: () => (options.value.labels || []).join(', '),
  set: value => {
    options.value.labels = value
      .split(',')
      .map(l => l.trim())
      .filter(Boolean);
    touch();
  },
});

const parsedTriggerLabels = computed(() => options.value.labels || []);

const toggleInbox = inboxId => {
  const current = options.value.inbox_ids || [];
  options.value.inbox_ids = current.includes(inboxId)
    ? current.filter(id => id !== inboxId)
    : [...current, inboxId];
  touch();
};

/* ------------------------------------------------------------------- chat */
const chatMessages = computed(() => options.value.messages || []);
const msgSettingsIdx = ref(null);
const btnPopover = ref(null);

const toggleMsgSettings = idx => {
  msgSettingsIdx.value = msgSettingsIdx.value === idx ? null : idx;
};

const openBtnPopover = (msgIdx, bIdx) => {
  const msg = chatMessages.value[msgIdx];
  const button = msg?.options?.buttons?.[bIdx];
  if (button) btnPopover.value = { msgIdx, bIdx, button };
};

const textMessage = computed(() =>
  chatMessages.value.find(m => m.name === 'send-text-message')
);

const inputMessage = computed(() =>
  chatMessages.value.find(m => m.name === 'text-input-message')
);

const waitsForReply = computed({
  get: () => Boolean(inputMessage.value),
  set: value => {
    const messages = options.value.messages || [];
    if (value) {
      messages.push({
        name: 'text-input-message',
        options: {
          parameter: '',
          text: '',
          timeoutInSeconds: 3600,
          timeoutWaitType: 'minutes',
          timeoutNextBlockId: '',
          invalidResponseMessage: 'Resposta inválida, tente novamente.',
          acceptMediaUrl: true,
        },
      });
    } else {
      options.value.messages = messages.filter(
        m => m.name !== 'text-input-message'
      );
    }
    touch();
  },
});

const timeoutMinutes = computed({
  get: () =>
    Math.round((inputMessage.value?.options?.timeoutInSeconds || 0) / 60),
  set: value => {
    if (inputMessage.value)
      inputMessage.value.options.timeoutInSeconds = Number(value) * 60;
    touch();
  },
});

const addButton = () => {
  textMessage.value.options.buttons = textMessage.value.options.buttons || [];
  textMessage.value.options.buttons.push({
    id: uid(),
    text: '',
    type: 'button',
    url: '',
    nextBlockId: '',
  });
  touch();
};

const addChatMessage = type => {
  const messages = options.value.messages || (options.value.messages = []);
  const defaults = {
    'send-text-message': { text: '', buttons: [], breakMessages: false, breakMessagesIntervalInSeconds: 3 },
    'text-input-message': { parameter: '', text: '', timeoutInSeconds: 3600, timeoutWaitType: 'minutes', timeoutNextBlockId: '', invalidResponseMessage: 'Resposta inválida, tente novamente.', acceptMediaUrl: true },
    'send-file-message': { url: '', text: '' },
    'send-url-file-message': { url: '' },
    'send-audio-message': { audios: [''] },
    'delay-message': { seconds: 3 },
  };
  messages.push({ name: type, options: defaults[type] || {} });
  touch();
};

const chatMsgLabel = name =>
  CHAT_MESSAGE_TYPES.find(t => t.value === name)?.label || name;

const chatMsgIcon = name => {
  const map = {
    'send-text-message': 'i-lucide-message-circle',
    'text-input-message': 'i-lucide-keyboard',
    'send-file-message': 'i-lucide-paperclip',
    'send-url-file-message': 'i-lucide-link',
    'send-audio-message': 'i-lucide-mic',
    'delay-message': 'i-lucide-hourglass',
  };
  return map[name] || 'i-lucide-circle';
};

/* ----------------------------------------------------------------- action */
const ACTION_GROUPS = [
  { key: 'etiqueta', label: 'Etiquetas' },
  { key: 'atendimento', label: 'Atendente / Time' },
  { key: 'conversa', label: 'Conversa' },
  { key: 'nota', label: 'Notas' },
  { key: 'contato', label: 'Contato / Lead' },
  { key: 'negocio', label: 'Negócio / Kanban' },
  { key: 'produto', label: 'Produto' },
  { key: 'sistema', label: 'Sistema' },
  { key: 'automacao', label: 'Automação' },
];

const addAction = () => {
  options.value.actions = options.value.actions || [];
  options.value.actions.push({ name: 'add_label', options: {} });
  touch();
};

const actionFields = action =>
  ACTION_TYPES.find(t => t.value === action.name)?.fields || [];

const labelsText = action => {
  const raw = action.options.labels;
  if (Array.isArray(raw)) return raw.join(', ');
  return raw || '';
};

const parsedLabels = action => {
  const raw = action.options.labels;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return raw.split(',').map(s => s.trim()).filter(Boolean);
};

const isLabelSelected = (action, title) =>
  parsedLabels(action).includes(title);

const toggleActionLabel = (action, title) => {
  const current = [...parsedLabels(action)];
  const idx = current.indexOf(title);
  if (idx >= 0) current.splice(idx, 1);
  else current.push(title);
  action.options.labels = current.join(', ');
  touch();
};

const removeActionLabel = (action, title) => {
  const current = parsedLabels(action).filter(l => l !== title);
  action.options.labels = current.join(', ');
  touch();
};

const filteredLabelsForAction = computed(() => {
  const q = labelSearch.value.toLowerCase();
  return (allLabels.value || []).filter(l =>
    l.title.toLowerCase().includes(q)
  );
});

const resetAction = action => {
  action.options = {};
  touch();
};

/* -------------------------------------------------------------- condition */
const openConditionPicker = () => {
  emit('add-condition', props.block.id);
};

const conditionMeta = condition =>
  CONDITION_TYPES.find(t => t.value === condition.name) || {};

/* ------------------------------------------------------------------ delay */
const delayUnit = computed({
  get: () => options.value.delay?.name || 'minutes-delay',
  set: value => {
    const unit = DELAY_UNITS.find(u => u.value === value);
    const current = Object.values(options.value.delay?.options || {})[0] || 0;
    options.value.delay = { name: value, options: { [unit.key]: current } };
    touch();
  },
});

const delayValue = computed({
  get: () => Object.values(options.value.delay?.options || {})[0] || 0,
  set: value => {
    const unit = DELAY_UNITS.find(u => u.value === delayUnit.value);
    options.value.delay = {
      name: delayUnit.value,
      options: { [unit.key]: Number(value) },
    };
    touch();
  },
});

/* -------------------------------------------------------------------- api */
const apiList = computed(() => options.value.apis || []);

const activeApiIndex = ref(0);
const apiOptions = computed(() => apiList.value[activeApiIndex.value]?.options || {});

const datasourceHint = computed(
  () => `{valor|[${apiOptions.value.datasourceName || 'Api-request-1'}]campo}`
);

const addApiRequest = () => {
  const apis = options.value.apis || (options.value.apis = []);
  const idx = apis.length + 1;
  apis.push({
    name: 'json-http-request-api',
    options: {
      url: '',
      method: 'GET',
      headers: [],
      query: [],
      body: '',
      datasourceName: `Api-request-${idx}`,
      retryCount: 0,
      retryIntervalSeconds: 5,
    },
  });
  activeApiIndex.value = apis.length - 1;
  touch();
};

const addPair = collection => {
  apiOptions.value[collection] = apiOptions.value[collection] || [];
  apiOptions.value[collection].push({ key: '', value: '' });
  touch();
};

/* -------------------------------------------------------------- randomizer */
const addBranch = () => {
  options.value.randomizers = options.value.randomizers || [];
  const index = options.value.randomizers.length;
  options.value.randomizers.push({
    id: uid(),
    name: String.fromCharCode(65 + index),
    perc: 0,
    nextBlockId: '',
  });
  touch();
};

/* --------------------------------------------------------- field operation */
const addFieldOperation = () => {
  options.value.fieldOperations = options.value.fieldOperations || [];
  options.value.fieldOperations.push({
    name: 'set-field-operation',
    group: 'field',
    stepId: uid(),
    options: { parameter: '', value: '' },
  });
  touch();
};

const removeAt = (collection, index) => {
  collection.splice(index, 1);
  touch();
};

/* ------------------------------------------------------------ ai-agent */
const aiActionRoutesList = computed(() =>
  Object.keys(options.value.action_routes || {})
);

const addAiActionRoute = () => {
  if (!options.value.action_routes) options.value.action_routes = {};
  const name = `ACAO_${Object.keys(options.value.action_routes).length + 1}`;
  options.value.action_routes[name] = '';
  touch();
};

const removeAiActionRoute = action => {
  if (options.value.action_routes) {
    delete options.value.action_routes[action];
    touch();
  }
};

const renameAiActionRoute = (oldName, newName) => {
  if (!newName || !options.value.action_routes) return;
  const target = options.value.action_routes[oldName] || '';
  delete options.value.action_routes[oldName];
  options.value.action_routes[newName.toUpperCase()] = target;
  touch();
};
</script>

<template>
  <aside class="dc-panel">
    <div class="dc-panel-inner">
      <!-- Header: p-4, back arrow + H4 title (DataCrazy exact) -->
      <div class="dc-panel-header">
        <div class="dc-panel-title-row">
          <button class="dc-back-arrow" @click="emit('close')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          </button>
          <span class="dc-panel-title">{{ $t(definition.labelKey) }}</span>
        </div>
      </div>

      <!-- Body: overflow-y-auto p-4 pt-0 scrollbar-thin flex-1 (DataCrazy exact) -->
      <div class="dc-panel-body">
        <p class="dc-panel-subtitle">{{ $t(definition.descriptionKey) }}</p>
      <!-- ============================================ TRIGGER -->
      <template v-if="block.type === 'trigger'">
        <div class="inspector-section">
          <label class="inspector-label">
            {{ $t('FLOWS.INSPECTOR.INBOXES') }}
          </label>
          <p class="inspector-hint">
            {{ $t('FLOWS.INSPECTOR.INBOXES_HINT') }}
          </p>
          <div class="flex flex-col gap-1.5 max-h-40 overflow-y-auto mt-2">
            <label
              v-for="inbox in inboxes"
              :key="inbox.id"
              class="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-n-alpha-1 cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                class="inspector-checkbox"
                :checked="(options.inbox_ids || []).includes(inbox.id)"
                @change="toggleInbox(inbox.id)"
              />
              <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
            </label>
          </div>
        </div>

        <div class="inspector-section">
          <label class="inspector-label">
            {{ $t('FLOWS.INSPECTOR.KEYWORDS') }}
          </label>
          <p class="inspector-hint">
            {{ $t('FLOWS.INSPECTOR.KEYWORDS_HINT') }}
          </p>
          <VarInput
            v-model="keywordsText"
            :placeholder="$t('FLOWS.INSPECTOR.KEYWORDS_PLACEHOLDER')"
          />
        </div>

        <div class="inspector-section">
          <label class="inspector-label">
            {{ $t('FLOWS.INSPECTOR.MATCH_TYPE') }}
          </label>
          <select
            v-model="options.match_type"
            class="inspector-select"
            @change="touch"
          >
            <option value="contains">
              {{ $t('FLOWS.INSPECTOR.MATCH_CONTAINS') }}
            </option>
            <option value="exact">
              {{ $t('FLOWS.INSPECTOR.MATCH_EXACT') }}
            </option>
          </select>
        </div>

        <div class="inspector-section">
          <label class="inspector-label">Condição de início da sessão</label>
          <p class="inspector-hint">Indica as condições para iniciar uma nova sessão na automação</p>
          <select
            v-model="options.initializeSession"
            class="inspector-select"
            @change="touch"
          >
            <option value="always">Reiniciar a cada mensagem</option>
            <option value="once">Iniciar apenas uma vez</option>
            <option value="only-if-finished">Iniciar apenas se já finalizado</option>
            <option value="only-if-finished-service">Iniciar apenas se o atendimento finalizou</option>
          </select>
        </div>

        <div class="inspector-section">
          <label class="inspector-label">Etiquetas obrigatórias</label>
          <p class="inspector-hint">
            Se preenchido, o flow só inicia se a conversa ou contato tiver pelo menos uma destas etiquetas
          </p>
          <VarInput
            v-model="triggerLabelsText"
            placeholder="cltpresença, neocredito"
            @change="touch"
          />
          <div v-if="parsedTriggerLabels.length" class="label-chips mt-1">
            <span
              v-for="lbl in parsedTriggerLabels"
              :key="lbl"
              class="label-chip"
            >
              {{ lbl }}
            </span>
          </div>
        </div>

        <div class="inspector-section">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              class="inspector-checkbox"
              :checked="options.listen_groups"
              @change="options.listen_groups = !options.listen_groups; touch()"
            />
            <span class="text-sm text-n-slate-12">Ouvir mensagens enviadas em grupos</span>
          </label>
        </div>

        <div class="inspector-section">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              class="inspector-checkbox"
              :checked="options.receive_datasource !== false"
              @change="options.receive_datasource = !(options.receive_datasource !== false); touch()"
            />
            <span class="text-sm text-n-slate-12">Receber fonte de dados do provedor da mensagem</span>
          </label>
          <div v-if="options.receive_datasource !== false" class="mt-2">
            <label class="inspector-label">Fonte de dados da mensagem</label>
            <input
              v-model="options.datasource_name"
              type="text"
              class="inspector-input"
              placeholder="Message-1"
              @input="touch"
            />
          </div>
        </div>
      </template>

      <!-- =============================================== CHAT (DataCrazy layout) -->
      <template v-else-if="block.type === 'chat'">
        <!-- Messages list: flex flex-col gap-2 mt-4 (DataCrazy exact) -->
        <div class="dc-msg-list">
          <div
            v-for="(msg, msgIdx) in chatMessages"
            :key="msgIdx"
            class="dc-msg-row"
          >
            <!-- Left: message content (flex-1) -->
            <div class="dc-msg-content">
              <!-- send-text-message -->
              <template v-if="msg.name === 'send-text-message'">
                <VarInput
                  v-model="msg.options.text"
                  type="textarea"
                  :rows="4"
                  :placeholder="$t('FLOWS.INSPECTOR.MESSAGE_PLACEHOLDER')"
                  @change="touch"
                />
                <!-- Buttons (DataCrazy style: ghost dashed) -->
                <div class="flex flex-col gap-1 mt-1">
                  <button
                    v-for="(button, bIdx) in msg.options.buttons || []"
                    :key="button.id"
                    type="button"
                    class="dc-msg-btn"
                    @click="openBtnPopover(msgIdx, bIdx)"
                  >
                    <span class="dc-msg-btn-icon">
                      <span :class="button.type === 'link' ? 'i-lucide-link' : 'i-lucide-square'" class="w-3.5 h-3.5" />
                    </span>
                    <span class="dc-msg-btn-text">{{ button.text || 'Botão' }}</span>
                    <span class="dc-msg-btn-del" @click.stop="removeAt(msg.options.buttons, bIdx)">
                      <span class="i-lucide-x w-3 h-3" />
                    </span>
                  </button>
                  <button
                    type="button"
                    class="dc-msg-btn dc-msg-btn-add"
                    @click="msg.options.buttons = msg.options.buttons || []; msg.options.buttons.push({ id: uid(), text: '', type: 'button', url: '', nextBlockId: '' }); touch()"
                  >
                    <span class="i-lucide-plus w-3.5 h-3.5" />
                    <span>Adicionar botão</span>
                  </button>
                </div>
              </template>

              <!-- text-input-message -->
              <template v-else-if="msg.name === 'text-input-message'">
                <div class="space-y-2">
                  <div>
                    <label class="inspector-label">Salvar resposta na variável</label>
                    <VarInput v-model="msg.options.parameter" placeholder="nome_da_variavel" @change="touch" />
                  </div>
                  <div>
                    <label class="inspector-label">Mensagem exibida ao contato</label>
                    <VarInput v-model="msg.options.text" type="textarea" :rows="2" placeholder="Ex: Por favor, digite seu CPF..." @change="touch" />
                  </div>
                  <div class="flex gap-3 items-end">
                    <div class="flex-1">
                      <label class="inspector-label">Tempo de espera</label>
                      <input v-model.number="msg.options.timeoutInSeconds" type="number" min="0" class="inspector-input" @input="touch" />
                    </div>
                    <div class="flex-1">
                      <label class="inspector-label">Unidade</label>
                      <select v-model="msg.options.timeoutWaitType" class="inspector-select" @change="touch">
                        <option value="seconds">Segundos</option>
                        <option value="minutes">Minutos</option>
                        <option value="hours">Horas</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label class="inspector-label">Mensagem de resposta inválida</label>
                    <VarInput v-model="msg.options.invalidResponseMessage" type="textarea" :rows="2" placeholder="Resposta inválida, tente novamente..." @change="touch" />
                  </div>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" class="inspector-checkbox" :checked="msg.options.acceptMediaUrl !== false" @change="msg.options.acceptMediaUrl = !(msg.options.acceptMediaUrl !== false); touch()" />
                    <span class="text-xs text-n-slate-12">Aceitar mídia como resposta</span>
                  </label>
                </div>
              </template>

              <!-- send-file-message -->
              <template v-else-if="msg.name === 'send-file-message'">
                <div class="space-y-2">
                  <div>
                    <label class="inspector-label">URL do arquivo</label>
                    <VarInput v-model="msg.options.url" placeholder="https://..." @change="touch" />
                  </div>
                  <div>
                    <label class="inspector-label">Legenda (opcional)</label>
                    <VarInput v-model="msg.options.text" placeholder="Texto da legenda" @change="touch" />
                  </div>
                </div>
              </template>

              <!-- send-url-file-message -->
              <template v-else-if="msg.name === 'send-url-file-message'">
                <div>
                  <label class="inspector-label">URL do arquivo</label>
                  <VarInput v-model="msg.options.url" placeholder="https://..." @change="touch" />
                </div>
              </template>

              <!-- send-audio-message -->
              <template v-else-if="msg.name === 'send-audio-message'">
                <div class="space-y-2">
                  <div v-for="(audioUrl, aIdx) in msg.options.audios || ['']" :key="aIdx" class="flex items-center gap-2">
                    <VarInput :model-value="audioUrl" placeholder="URL do áudio" @update:model-value="msg.options.audios[aIdx] = $event; touch()" />
                    <button v-if="(msg.options.audios || []).length > 1" class="inspector-remove-btn" @click="msg.options.audios.splice(aIdx, 1); touch()">
                      <span class="i-lucide-x w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button class="inspector-add-btn" @click="msg.options.audios = msg.options.audios || []; msg.options.audios.push(''); touch()">
                    <span class="i-lucide-plus w-3 h-3" /> Mais áudio
                  </button>
                </div>
              </template>

              <!-- delay-message -->
              <template v-else-if="msg.name === 'delay-message'">
                <div>
                  <label class="inspector-label">Segundos digitando...</label>
                  <input v-model.number="msg.options.seconds" type="number" min="0" max="10" class="inspector-input" @input="touch" />
                </div>
              </template>
            </div>

            <!-- Right: action icons column (DataCrazy exact) -->
            <div class="dc-msg-actions">
              <button
                v-if="msg.name === 'send-text-message'"
                type="button"
                class="dc-msg-action-btn"
                title="Opções"
                @click="toggleMsgSettings(msgIdx)"
              >
                <span class="i-lucide-settings w-4 h-4" />
              </button>
              <span class="dc-msg-action-btn dc-msg-drag" title="Arrastar">
                <span class="i-lucide-grip-vertical w-4 h-4" />
              </span>
              <button
                v-if="chatMessages.length > 1"
                type="button"
                class="dc-msg-action-btn dc-msg-action-del"
                title="Remover"
                @click="removeAt(options.messages, msgIdx)"
              >
                <span class="i-lucide-trash-2 w-4 h-4" />
              </button>
            </div>
          </div>

          <!-- Inline settings popover for break messages -->
          <div v-if="msgSettingsIdx !== null && chatMessages[msgSettingsIdx]?.name === 'send-text-message'" class="dc-msg-settings">
            <div class="dc-msg-settings-inner">
              <p class="text-xs font-semibold text-n-slate-12 mb-2">Opções da mensagem</p>
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" v-model="chatMessages[msgSettingsIdx].options.breakMessages" class="inspector-checkbox" @change="touch" />
                <span class="text-xs text-n-slate-11">Quebrar em múltiplas mensagens</span>
              </label>
              <div v-if="chatMessages[msgSettingsIdx].options.breakMessages" class="mt-2">
                <label class="inspector-label">Intervalo entre mensagens (seg)</label>
                <input v-model.number="chatMessages[msgSettingsIdx].options.breakMessagesIntervalInSeconds" type="number" min="0" max="30" class="inspector-input w-20" placeholder="3" @input="touch" />
              </div>
            </div>
          </div>
        </div>

        <!-- Button settings inline panel -->
        <div v-if="btnPopover" class="dc-btn-settings">
          <div class="dc-btn-settings-inner">
            <div class="flex items-center justify-between mb-3">
              <p class="text-sm font-semibold text-n-slate-12">Configurações do botão</p>
              <button class="dc-back-btn" @click="btnPopover = null">
                <span class="i-lucide-x w-4 h-4" />
              </button>
            </div>
            <div class="space-y-3">
              <div>
                <label class="inspector-label">Texto do botão</label>
                <VarInput v-model="btnPopover.button.text" placeholder="Texto do botão" @change="touch" />
                <p class="text-[11px] text-n-slate-9 mt-1 ml-0.5">{{ (btnPopover.button.text || '').length }}/20</p>
              </div>
              <div>
                <label class="inspector-label">Tipo do botão</label>
                <select v-model="btnPopover.button.type" class="inspector-select" @change="touch">
                  <option value="button">Botão</option>
                  <option value="link">Link</option>
                </select>
              </div>
              <div v-if="btnPopover.button.type === 'link'">
                <label class="inspector-label">URL</label>
                <VarInput v-model="btnPopover.button.url" placeholder="https://..." @change="touch" />
              </div>
            </div>
          </div>
        </div>

        <!-- Add message type (DataCrazy style) -->
        <div class="chat-add-wrap">
          <label class="inspector-label">Adicionar passo</label>
          <div class="chat-add-grid">
            <button
              v-for="mtype in CHAT_MESSAGE_TYPES"
              :key="mtype.value"
              class="chat-add-item"
              @click="addChatMessage(mtype.value)"
            >
              <span :class="chatMsgIcon(mtype.value)" class="w-4 h-4" />
              <span>{{ mtype.label }}</span>
            </button>
          </div>
        </div>
      </template>

      <!-- ============================================= ACTION -->
      <template v-else-if="block.type === 'action'">
        <div class="flex items-center justify-between">
          <label class="inspector-label !mb-0">
            {{ $t('FLOWS.INSPECTOR.ACTIONS') }}
          </label>
          <button class="inspector-add-btn" @click="addAction">
            <span class="i-lucide-plus w-3 h-3" />
            {{ $t('FLOWS.INSPECTOR.ADD_ACTION') }}
          </button>
        </div>

        <div
          v-for="(action, index) in options.actions || []"
          :key="index"
          class="inspector-card"
        >
          <div class="flex items-center gap-2">
            <select
              v-model="action.name"
              class="inspector-select !mb-0"
              @change="resetAction(action)"
            >
              <optgroup
                v-for="group in ACTION_GROUPS"
                :key="group.key"
                :label="group.label"
              >
                <option
                  v-for="type in ACTION_TYPES.filter(t => t.group === group.key)"
                  :key="type.value"
                  :value="type.value"
                >
                  {{ type.label }}
                </option>
              </optgroup>
            </select>
            <button
              class="inspector-remove-btn"
              :title="$t('FLOWS.INSPECTOR.REMOVE')"
              @click="removeAt(options.actions, index)"
            >
              <span class="i-lucide-x w-3.5 h-3.5" />
            </button>
          </div>

          <template v-for="field in actionFields(action)" :key="field">
            <div v-if="field === 'labels'" class="label-selector">
              <div
                v-if="parsedLabels(action).length"
                class="label-chips"
              >
                <span
                  v-for="lbl in parsedLabels(action)"
                  :key="lbl"
                  class="label-chip"
                >
                  {{ lbl }}
                  <button
                    class="label-chip-x"
                    @click="removeActionLabel(action, lbl)"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                  </button>
                </span>
              </div>
              <div class="label-search-wrap">
                <svg class="label-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                <input
                  v-model="labelSearch"
                  type="text"
                  class="label-search-input"
                  placeholder="Buscar etiqueta..."
                />
              </div>
              <div class="label-list">
                <button
                  v-for="label in filteredLabelsForAction"
                  :key="label.id"
                  class="label-list-item"
                  :class="{ 'label-list-item-selected': isLabelSelected(action, label.title) }"
                  @click="toggleActionLabel(action, label.title)"
                >
                  <span
                    class="label-dot"
                    :style="{ background: label.color || '#6366f1' }"
                  />
                  <span class="label-item-name">{{ label.title }}</span>
                  <svg
                    v-if="isLabelSelected(action, label.title)"
                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2.5"
                    stroke-linecap="round" stroke-linejoin="round"
                    class="label-check"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </button>
                <p
                  v-if="!filteredLabelsForAction.length"
                  class="label-empty"
                >
                  Nenhuma etiqueta encontrada
                </p>
              </div>
            </div>
            <select
              v-else-if="field === 'agent_id'"
              v-model="action.options.agent_id"
              class="inspector-select !mb-0"
              @change="touch"
            >
              <option value="">{{ $t('FLOWS.INSPECTOR.NO_AGENT') }}</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
            <select
              v-else-if="field === 'team_id'"
              v-model="action.options.team_id"
              class="inspector-select !mb-0"
              @change="touch"
            >
              <option value="">{{ $t('FLOWS.INSPECTOR.NO_TEAM') }}</option>
              <option v-for="team in teams" :key="team.id" :value="team.id">
                {{ team.name }}
              </option>
            </select>
            <select
              v-else-if="field === 'status'"
              v-model="action.options.status"
              class="inspector-select !mb-0"
              @change="touch"
            >
              <option
                v-for="status in CONVERSATION_STATUSES"
                :key="status"
                :value="status"
              >
                {{ status }}
              </option>
            </select>
            <select
              v-else-if="field === 'priority'"
              v-model="action.options.priority"
              class="inspector-select !mb-0"
              @change="touch"
            >
              <option
                v-for="priority in PRIORITIES"
                :key="priority"
                :value="priority"
              >
                {{ priority || $t('FLOWS.INSPECTOR.NO_PRIORITY') }}
              </option>
            </select>
            <VarInput
              v-else-if="field === 'content'"
              v-model="action.options.content"
              type="textarea"
              :rows="3"
              :placeholder="$t('FLOWS.INSPECTOR.NOTE_PLACEHOLDER')"
              @change="touch"
            />
            <select
              v-else-if="field === 'stageId'"
              v-model="action.options.stageId"
              class="inspector-select !mb-0"
              @change="touch"
            >
              <option value="">Selecione a etapa</option>
              <option
                v-for="stage in allStages"
                :key="stage.id"
                :value="stage.id"
              >
                {{ stage.name }}
              </option>
            </select>
            <VarInput
              v-else
              v-model="action.options[field]"
              :placeholder="field"
              @change="touch"
            />
          </template>
        </div>
      </template>

      <!-- ========================================== CONDITION -->
      <template v-else-if="block.type === 'condition'">
        <div class="flex items-center justify-between">
          <label class="inspector-label !mb-0">
            {{ $t('FLOWS.INSPECTOR.CONDITIONS') }}
          </label>
          <button class="inspector-add-btn" @click="openConditionPicker">
            <span class="i-lucide-plus w-3 h-3" />
            {{ $t('FLOWS.INSPECTOR.ADD') }}
          </button>
        </div>

        <div
          v-for="(condition, index) in options.conditions || []"
          :key="condition.id"
          class="inspector-card"
        >
          <div class="flex items-center gap-2">
            <select
              v-model="condition.name"
              class="inspector-select !mb-0"
              @change="touch"
            >
              <option
                v-for="type in CONDITION_TYPES"
                :key="type.value"
                :value="type.value"
              >
                {{ type.label }}
              </option>
            </select>
            <button
              class="inspector-remove-btn"
              :title="$t('FLOWS.INSPECTOR.REMOVE')"
              @click="removeAt(options.conditions, index)"
            >
              <span class="i-lucide-x w-3.5 h-3.5" />
            </button>
          </div>

          <VarInput
            v-if="!conditionMeta(condition).noParameter"
            v-model="condition.options.parameter"
            :placeholder="$t('FLOWS.INSPECTOR.PARAMETER_PLACEHOLDER')"
            @change="touch"
          />
          <select
            v-if="conditionMeta(condition).valueKey === 'pipelineId'"
            v-model="condition.options.pipelineId"
            class="inspector-select !mb-0"
            @change="touch"
          >
            <option value="">Selecione o pipeline</option>
            <option
              v-for="pipeline in pipelines"
              :key="pipeline.id"
              :value="pipeline.id"
            >
              {{ pipeline.name }}
            </option>
          </select>
          <select
            v-else-if="conditionMeta(condition).valueKey === 'stageId'"
            v-model="condition.options.stageId"
            class="inspector-select !mb-0"
            @change="touch"
          >
            <option value="">Selecione a etapa</option>
            <option
              v-for="stage in allStages"
              :key="stage.id"
              :value="stage.id"
            >
              {{ stage.name }}
            </option>
          </select>
          <!-- between-condition: two numeric fields -->
          <div v-else-if="condition.name === 'between-condition'" class="flex gap-2 items-center">
            <div class="flex-1">
              <label class="inspector-label">Min</label>
              <input
                v-model.number="condition.options.greaterOrEqualsTo"
                type="number"
                class="inspector-input !mb-0"
                placeholder="0"
                @input="touch"
              />
            </div>
            <span class="text-xs text-n-slate-10 mt-5">a</span>
            <div class="flex-1">
              <label class="inspector-label">Max</label>
              <input
                v-model.number="condition.options.lessOrEqualsTo"
                type="number"
                class="inspector-input !mb-0"
                placeholder="9999"
                @input="touch"
              />
            </div>
          </div>
          <!-- business-with-external-id: externalId field -->
          <VarInput
            v-else-if="conditionMeta(condition).valueKey === 'externalId'"
            v-model="condition.options.externalId"
            placeholder="ID externo do negócio"
            @change="touch"
          />
          <!-- conversation-has-department: team selector -->
          <select
            v-else-if="conditionMeta(condition).valueKey === 'departmentId'"
            v-model="condition.options.departmentId"
            class="inspector-select !mb-0"
            @change="touch"
          >
            <option value="">Selecione o departamento</option>
            <option v-for="team in teams" :key="team.id" :value="team.id">
              {{ team.name }}
            </option>
          </select>
          <!-- additional field conditions: additionalFieldId + type + value -->
          <template v-else-if="conditionMeta(condition).valueKey === 'additionalFieldId'">
            <VarInput
              v-model="condition.options.additionalFieldId"
              placeholder="Nome do campo adicional"
              @change="touch"
            />
          </template>
          <template v-else-if="condition.name === 'lead-with-additional-field-exists-condition' || condition.name === 'business-with-additional-field-exists-condition'">
            <VarInput
              v-model="condition.options.additionalFieldId"
              placeholder="Nome do campo adicional"
              @change="touch"
            />
            <select v-model="condition.options.type" class="inspector-select !mb-0" @change="touch">
              <option value="equal">Igual a</option>
              <option value="contains">Contém</option>
            </select>
            <VarInput
              v-model="condition.options.value"
              placeholder="Valor esperado"
              @change="touch"
            />
          </template>
          <VarInput
            v-else-if="conditionMeta(condition).valueKey && conditionMeta(condition).valueKey !== 'pipelineId' && conditionMeta(condition).valueKey !== 'stageId' && conditionMeta(condition).valueKey !== 'greaterOrEqualsTo' && conditionMeta(condition).valueKey !== 'externalId' && conditionMeta(condition).valueKey !== 'departmentId' && conditionMeta(condition).valueKey !== 'additionalFieldId'"
            v-model="condition.options[conditionMeta(condition).valueKey]"
            :placeholder="$t('FLOWS.INSPECTOR.EXPECTED_PLACEHOLDER')"
            @change="touch"
          />
        </div>
      </template>

      <!-- ============================================== DELAY -->
      <template v-else-if="block.type === 'delay'">
        <div class="inspector-section">
          <label class="inspector-label">Tipo de espera</label>
          <select v-model="delayUnit" class="inspector-select">
            <option
              v-for="unit in DELAY_UNITS"
              :key="unit.value"
              :value="unit.value"
            >
              {{ $t(unit.labelKey) }}
            </option>
          </select>
        </div>

        <!-- Standard time delay -->
        <div v-if="!['until-date-delay', 'time-interval-delay'].includes(delayUnit)" class="inspector-section">
          <label class="inspector-label">
            {{ $t('FLOWS.INSPECTOR.WAIT_FOR') }}
          </label>
          <input
            v-model.number="delayValue"
            type="number"
            min="0"
            class="inspector-input !mb-0"
          />
        </div>

        <!-- Until date delay -->
        <template v-if="delayUnit === 'until-date-delay'">
          <div class="inspector-section">
            <label class="inspector-label">Data alvo (variável ou fixa)</label>
            <VarInput
              v-model="options.delay.options.date"
              placeholder="Ex: {Data|leadBirthDate} ou 2025-12-31"
              @change="touch"
            />
          </div>
          <div class="inspector-section">
            <div class="flex gap-3">
              <div class="flex-1">
                <label class="inspector-label">Hora início</label>
                <input
                  v-model="options.delay.options.startTime"
                  type="time"
                  class="inspector-input !mb-0"
                  @input="touch"
                />
              </div>
              <div class="flex-1">
                <label class="inspector-label">Hora fim</label>
                <input
                  v-model="options.delay.options.endTime"
                  type="time"
                  class="inspector-input !mb-0"
                  @input="touch"
                />
              </div>
            </div>
          </div>
          <div class="inspector-section">
            <label class="inspector-label">Fuso horário</label>
            <input
              v-model="options.delay.options.timezone"
              type="text"
              class="inspector-input"
              placeholder="America/Sao_Paulo"
              @input="touch"
            />
          </div>
        </template>

        <!-- Time interval delay (days of week) -->
        <template v-if="delayUnit === 'time-interval-delay'">
          <div class="inspector-section">
            <label class="inspector-label">Dias da semana</label>
            <div class="flex flex-wrap gap-2 mt-1">
              <label
                v-for="(dayLabel, dayIdx) in ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']"
                :key="dayIdx"
                class="dc-day-toggle"
                :class="(options.delay.options.daysOfWeek || []).includes(dayIdx) ? 'dc-day-active' : 'dc-day-inactive'"
              >
                <input
                  type="checkbox"
                  class="sr-only"
                  :checked="(options.delay.options.daysOfWeek || []).includes(dayIdx)"
                  @change="
                    const days = options.delay.options.daysOfWeek || [];
                    if (days.includes(dayIdx)) {
                      options.delay.options.daysOfWeek = days.filter(d => d !== dayIdx);
                    } else {
                      options.delay.options.daysOfWeek = [...days, dayIdx].sort();
                    }
                    touch();
                  "
                />
                {{ dayLabel }}
              </label>
            </div>
          </div>
          <div class="inspector-section">
            <div class="flex gap-3">
              <div class="flex-1">
                <label class="inspector-label">Hora início</label>
                <input
                  v-model="options.delay.options.startTime"
                  type="time"
                  class="inspector-input !mb-0"
                  @input="touch"
                />
              </div>
              <div class="flex-1">
                <label class="inspector-label">Hora fim</label>
                <input
                  v-model="options.delay.options.endTime"
                  type="time"
                  class="inspector-input !mb-0"
                  @input="touch"
                />
              </div>
            </div>
          </div>
          <div class="inspector-section">
            <label class="inspector-label">Fuso horário</label>
            <input
              v-model="options.delay.options.timezone"
              type="text"
              class="inspector-input"
              placeholder="America/Sao_Paulo"
              @input="touch"
            />
          </div>
        </template>
      </template>

      <!-- ================================================ API -->
      <template v-else-if="block.type === 'api'">
        <!-- API tabs when multiple requests -->
        <div v-if="apiList.length > 1" class="api-tabs">
          <button
            v-for="(api, aIdx) in apiList"
            :key="aIdx"
            class="api-tab"
            :class="{ 'api-tab-active': activeApiIndex === aIdx }"
            @click="activeApiIndex = aIdx"
          >
            {{ api.options?.datasourceName || `Request ${aIdx + 1}` }}
          </button>
          <button class="api-tab api-tab-add" @click="addApiRequest">
            <span class="i-lucide-plus w-3 h-3" />
          </button>
        </div>

        <div class="inspector-section">
          <div class="flex gap-3">
            <div class="w-28 shrink-0">
              <label class="inspector-label">
                {{ $t('FLOWS.INSPECTOR.METHOD') }}
              </label>
              <select
                v-model="apiOptions.method"
                class="inspector-select !mb-0 font-mono"
                @change="touch"
              >
                <option
                  v-for="method in HTTP_METHODS"
                  :key="method"
                  :value="method"
                >
                  {{ method }}
                </option>
              </select>
            </div>
            <div class="flex-1">
              <label class="inspector-label">
                {{ $t('FLOWS.INSPECTOR.URL') }}
              </label>
              <VarInput
                v-model="apiOptions.url"
                :placeholder="$t('FLOWS.INSPECTOR.URL_PLACEHOLDER')"
                :mono="true"
                @change="touch"
              />
            </div>
          </div>
        </div>

        <div class="inspector-section">
          <label class="inspector-label">
            {{ $t('FLOWS.INSPECTOR.RESULT_NAME') }}
          </label>
          <input
            v-model="apiOptions.datasourceName"
            type="text"
            class="inspector-input"
            @input="touch"
          />
          <p class="inspector-hint mt-1">
            {{ $t('FLOWS.INSPECTOR.RESULT_HINT') }}
            <code
              class="px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-12 text-[11px] font-mono"
            >
              {{ datasourceHint }}
            </code>
          </p>
        </div>

        <div class="inspector-section">
          <div class="flex items-center justify-between mb-2">
            <label class="inspector-label !mb-0">
              {{ $t('FLOWS.INSPECTOR.HEADERS') }}
            </label>
            <button class="inspector-add-btn" @click="addPair('headers')">
              <span class="i-lucide-plus w-3 h-3" />
              {{ $t('FLOWS.INSPECTOR.ADD') }}
            </button>
          </div>
          <div
            v-for="(header, index) in apiOptions.headers || []"
            :key="`h-${index}`"
            class="flex gap-2 mb-1.5 items-start"
          >
            <VarInput
              v-model="header.key"
              :placeholder="PLACEHOLDERS.headerKey"
              :mono="true"
              @change="touch"
            />
            <VarInput
              v-model="header.value"
              :placeholder="PLACEHOLDERS.headerValue"
              :mono="true"
              @change="touch"
            />
            <button
              class="inspector-remove-btn mt-1"
              :title="$t('FLOWS.INSPECTOR.REMOVE')"
              @click="removeAt(apiOptions.headers, index)"
            >
              <span class="i-lucide-x w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div class="inspector-section">
          <div class="flex items-center justify-between mb-2">
            <label class="inspector-label !mb-0">
              {{ $t('FLOWS.INSPECTOR.QUERY_PARAMS') }}
            </label>
            <button class="inspector-add-btn" @click="addPair('query')">
              <span class="i-lucide-plus w-3 h-3" />
              {{ $t('FLOWS.INSPECTOR.ADD') }}
            </button>
          </div>
          <div
            v-for="(param, index) in apiOptions.query || []"
            :key="`q-${index}`"
            class="flex gap-2 mb-1.5 items-start"
          >
            <VarInput
              v-model="param.key"
              :placeholder="PLACEHOLDERS.queryKey"
              :mono="true"
              @change="touch"
            />
            <VarInput
              v-model="param.value"
              :placeholder="PLACEHOLDERS.queryValue"
              :mono="true"
              @change="touch"
            />
            <button
              class="inspector-remove-btn mt-1"
              :title="$t('FLOWS.INSPECTOR.REMOVE')"
              @click="removeAt(apiOptions.query, index)"
            >
              <span class="i-lucide-x w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div v-if="apiOptions.method !== 'GET'" class="inspector-section">
          <label class="inspector-label">
            {{ $t('FLOWS.INSPECTOR.BODY') }}
          </label>
          <VarInput
            v-model="apiOptions.body"
            type="textarea"
            :rows="5"
            :placeholder="PLACEHOLDERS.body"
            :mono="true"
            @change="touch"
          />
        </div>

        <div class="inspector-section">
          <div class="flex gap-3">
            <div class="flex-1">
              <label class="inspector-label">
                {{ $t('FLOWS.INSPECTOR.RETRIES') }}
              </label>
              <input
                v-model.number="apiOptions.retryCount"
                type="number"
                min="0"
                max="5"
                class="inspector-input !mb-0"
                @input="touch"
              />
            </div>
            <div class="flex-1">
              <label class="inspector-label">
                {{ $t('FLOWS.INSPECTOR.RETRY_INTERVAL') }}
              </label>
              <input
                v-model.number="apiOptions.retryIntervalSeconds"
                type="number"
                min="0"
                max="30"
                class="inspector-input !mb-0"
                @input="touch"
              />
            </div>
          </div>
        </div>

        <!-- Add another API request button -->
        <button
          v-if="apiList.length <= 1"
          class="inspector-add-btn w-full justify-center"
          @click="addApiRequest"
        >
          <span class="i-lucide-plus w-3 h-3" />
          Adicionar requisição
        </button>

        <!-- Remove current API request -->
        <button
          v-if="apiList.length > 1"
          class="inspector-remove-btn w-full flex items-center justify-center gap-1 text-xs py-2"
          @click="options.apis.splice(activeApiIndex, 1); activeApiIndex = Math.max(0, activeApiIndex - 1); touch()"
        >
          <span class="i-lucide-trash-2 w-3 h-3" /> Remover esta requisição
        </button>
      </template>

      <!-- ========================================= RANDOMIZER -->
      <template v-else-if="block.type === 'randomizer'">
        <div class="flex items-center justify-between">
          <label class="inspector-label !mb-0">
            {{ $t('FLOWS.INSPECTOR.PATHS') }}
          </label>
          <button class="inspector-add-btn" @click="addBranch">
            <span class="i-lucide-plus w-3 h-3" />
            {{ $t('FLOWS.INSPECTOR.ADD') }}
          </button>
        </div>
        <div
          v-for="(branch, index) in options.randomizers || []"
          :key="branch.id"
          class="flex items-center gap-2"
        >
          <input
            v-model="branch.name"
            type="text"
            :placeholder="PLACEHOLDERS.branch"
            class="inspector-input !mb-0 w-20 text-center font-semibold"
            @input="touch"
          />
          <div class="flex-1 flex items-center gap-2">
            <input
              v-model.number="branch.perc"
              type="number"
              min="0"
              max="100"
              class="inspector-input !mb-0"
              @input="touch"
            />
            <span class="text-xs text-n-slate-11 font-medium shrink-0">{{
              PLACEHOLDERS.percent
            }}</span>
          </div>
          <button
            class="inspector-remove-btn"
            :title="$t('FLOWS.INSPECTOR.REMOVE')"
            @click="removeAt(options.randomizers, index)"
          >
            <span class="i-lucide-x w-3.5 h-3.5" />
          </button>
        </div>
      </template>

      <!-- =========================================== AI AGENT -->
      <template v-else-if="block.type === 'ai-agent'">
        <div class="inspector-section">
          <label class="inspector-label">Endpoint da IA</label>
          <p class="inspector-hint">URL do serviço que processa as mensagens (ex: http://127.0.0.1:6100/ai/process)</p>
          <VarInput
            v-model="options.endpoint"
            placeholder="http://127.0.0.1:6100/ai/process"
            :mono="true"
            @change="touch"
          />
        </div>

        <div class="inspector-section">
          <label class="inspector-label">Token de autenticação</label>
          <p class="inspector-hint">Bearer token enviado no header Authorization</p>
          <input
            v-model="options.auth_token"
            type="password"
            class="inspector-input"
            placeholder="Token secreto"
            @input="touch"
          />
        </div>

        <div class="inspector-section">
          <label class="inspector-label">Timeout entre mensagens (segundos)</label>
          <p class="inspector-hint">Tempo de espera antes de enviar um follow-up (padrao: 1800 = 30 min)</p>
          <input
            v-model.number="options.timeoutInSeconds"
            type="number"
            min="60"
            max="86400"
            class="inspector-input"
            @input="touch"
          />
        </div>

        <div class="inspector-section">
          <label class="inspector-label">Maximo de follow-ups</label>
          <p class="inspector-hint">Quantas mensagens de lembrete enviar antes de encerrar (padrao: 2). Apos o limite, encerra e vai para o bloco de timeout.</p>
          <input
            v-model.number="options.maxFollowups"
            type="number"
            min="0"
            max="5"
            class="inspector-input"
            @input="touch"
          />
        </div>

        <div class="inspector-section">
          <div class="flex items-center justify-between">
            <label class="inspector-label !mb-0">Rotas de ação</label>
            <button class="inspector-add-btn" @click="addAiActionRoute">
              <span class="i-lucide-plus w-3 h-3" />
              Adicionar
            </button>
          </div>
          <p class="inspector-hint">Ações retornadas pela IA que direcionam para blocos específicos (ex: ENCERRAR, TRANSFERIR)</p>
          <div
            v-for="(action, idx) in aiActionRoutesList"
            :key="idx"
            class="inspector-card"
          >
            <div class="flex items-center gap-2">
              <input
                :value="action"
                type="text"
                class="inspector-input !mb-0"
                placeholder="NOME_ACAO"
                @input="renameAiActionRoute(action, $event.target.value)"
              />
              <button
                class="inspector-remove-btn"
                title="Remover"
                @click="removeAiActionRoute(action)"
              >
                <span class="i-lucide-x w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </template>

      <!-- ==================================== FIELD OPERATION -->
      <template v-else-if="block.type === 'field-operation'">
        <div class="flex items-center justify-between">
          <label class="inspector-label !mb-0">
            {{ $t('FLOWS.INSPECTOR.OPERATIONS') }}
          </label>
          <button class="inspector-add-btn" @click="addFieldOperation">
            <span class="i-lucide-plus w-3 h-3" />
            {{ $t('FLOWS.INSPECTOR.ADD') }}
          </button>
        </div>
        <div
          v-for="(operation, index) in options.fieldOperations || []"
          :key="operation.stepId"
          class="inspector-card"
        >
          <div class="flex items-center gap-2">
            <select
              v-model="operation.name"
              class="inspector-select !mb-0"
              @change="touch"
            >
              <option value="set-field-operation">
                {{ $t('FLOWS.INSPECTOR.SET_VALUE') }}
              </option>
              <option value="clear-field-operation">
                {{ $t('FLOWS.INSPECTOR.CLEAR_VALUE') }}
              </option>
              <option value="loop-array-field-operation">
                Loop (iterar array)
              </option>
            </select>
            <button
              class="inspector-remove-btn"
              :title="$t('FLOWS.INSPECTOR.REMOVE')"
              @click="removeAt(options.fieldOperations, index)"
            >
              <span class="i-lucide-x w-3.5 h-3.5" />
            </button>
          </div>
          <VarInput
            v-model="operation.options.parameter"
            :placeholder="$t('FLOWS.INSPECTOR.FIELD_PLACEHOLDER')"
            @change="touch"
          />
          <VarInput
            v-if="operation.name === 'set-field-operation'"
            v-model="operation.options.value"
            :placeholder="$t('FLOWS.INSPECTOR.VALUE_PLACEHOLDER')"
            @change="touch"
          />
          <template v-if="operation.name === 'loop-array-field-operation'">
            <div>
              <label class="inspector-label">Fonte de dados (array)</label>
              <input
                v-model="operation.options.datasourceName"
                type="text"
                class="inspector-input"
                placeholder="Api-request-1"
                @input="touch"
              />
            </div>
          </template>
        </div>
      </template>
      </div>
    </div>
  </aside>
</template>

<style scoped>
/* ================================================================
   DataCrazy Design System — exact replica
   Primary: hsl(216 100% 60%) = #3385ff
   Border:  hsl(214 32% 91%)
   Input:   hsl(210 11% 86%)
   Radius:  0.5rem (8px)
   ================================================================ */

/* --- Panel layout (DataCrazy eb component) --- */
.dc-panel {
  display: flex;
  flex-direction: column;
  width: 36.25rem;
  height: 100%;
  border-right: 1px solid hsl(214 32% 91%);
  background: white;
  overflow: hidden;
  flex-shrink: 0;
}
.dc-panel-inner {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.dc-panel-header {
  padding: 1rem;
  flex-shrink: 0;
}
.dc-panel-title-row {
  display: flex;
  gap: 0.375rem;
  align-items: center;
}
.dc-back-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: hsl(215 16% 47%);
  cursor: pointer;
  padding: 0.125rem;
  transition: color 0.12s ease;
}
.dc-back-arrow:hover {
  color: hsl(222 84% 5%);
}
.dc-panel-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: hsl(222 84% 5%);
  user-select: none;
}
.dc-panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 1rem 1rem;
  scrollbar-width: thin;
  scrollbar-color: #e2e2e2 transparent;
}
.dc-panel-body::-webkit-scrollbar { width: 2px; }
.dc-panel-body::-webkit-scrollbar-thumb { background-color: #e2e2e2; border-radius: 5px; }
.dc-panel-body::-webkit-scrollbar-track { background-color: transparent; }
.dc-panel-subtitle {
  font-size: 0.75rem;
  color: hsl(215 16% 47%);
  margin-bottom: 0.75rem;
  user-select: none;
}

/* --- Form elements (DataCrazy exact) --- */
.inspector-section {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}
.inspector-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 400;
  color: hsl(215 16% 47%);
  margin-left: 0.125rem;
  margin-bottom: 0.25rem;
}
.inspector-hint {
  font-size: 0.625rem;
  color: hsl(215 16% 47%);
  font-weight: 300;
  margin-left: 0.125rem;
  line-height: 1.5;
}
.inspector-input {
  width: 100%;
  height: 2.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: hsl(222 84% 5%);
  background: white;
  border: 1px solid hsl(210 11% 86%);
  border-radius: 0.375rem;
  outline: none;
  transition: border-color 0.15s ease;
}
.inspector-input:focus {
  border-color: #3385ff;
}
.inspector-input::placeholder {
  color: hsl(215 16% 47%);
}
.inspector-textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: hsl(222 84% 5%);
  background: white;
  border: 1px solid hsl(210 11% 86%);
  border-radius: 0.375rem;
  outline: none;
  transition: border-color 0.15s ease;
  resize: vertical;
}
.inspector-textarea:focus {
  border-color: #3385ff;
}
.inspector-textarea::placeholder {
  color: hsl(215 16% 47%);
}
.inspector-select {
  width: 100%;
  height: 2.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: hsl(222 84% 5%);
  background: white;
  border: 1px solid hsl(210 11% 86%);
  border-radius: 0.375rem;
  outline: none;
  transition: border-color 0.15s ease;
  cursor: pointer;
}
.inspector-select:focus {
  border-color: #3385ff;
}
.inspector-checkbox {
  width: 1rem;
  height: 1rem;
  border-radius: 0.25rem;
  accent-color: #3385ff;
}
.inspector-card {
  border: 1px solid hsl(214 32% 91%);
  border-radius: 0.5rem;
  background: white;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  transition: border-color 0.15s ease;
}
.inspector-card:hover {
  border-color: #3385ff;
}
.inspector-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #3385ff;
  background: transparent;
  border: 1px dashed #3385ff;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background 0.12s ease;
}
.inspector-add-btn:hover {
  background: rgba(51, 133, 255, 0.05);
}
.inspector-remove-btn {
  padding: 0.25rem;
  border-radius: 0.375rem;
  color: hsl(215 16% 47%);
  transition: all 0.12s ease;
  flex-shrink: 0;
  cursor: pointer;
}
.inspector-remove-btn:hover {
  color: hsl(0 84% 60%);
  background: hsla(0, 84%, 60%, 0.08);
}

/* --- Label selector --- */
.label-selector { display: flex; flex-direction: column; gap: 0.5rem; }
.label-chips { display: flex; flex-wrap: wrap; gap: 0.25rem; }
.label-chip {
  display: inline-flex; align-items: center; gap: 0.25rem;
  padding: 0.2rem 0.5rem; border-radius: 0.375rem;
  background: rgba(51, 133, 255, 0.12); color: #3385ff;
  font-size: 0.75rem; font-weight: 600;
}
.label-chip-x { display: flex; align-items: center; color: #3385ff; cursor: pointer; padding: 0; }
.label-chip-x:hover { color: hsl(0 84% 60%); }
.label-search-wrap { position: relative; }
.label-search-icon { position: absolute; left: 0.625rem; top: 50%; transform: translateY(-50%); color: hsl(206 6% 56%); pointer-events: none; }
.label-search-input {
  width: 100%; padding: 0.375rem 0.625rem 0.375rem 2rem;
  font-size: 0.8rem; color: hsl(222 84% 5%); background: white;
  border: 1px solid hsl(210 11% 86%); border-radius: 0.375rem; outline: none;
}
.label-search-input:focus { border-color: #3385ff; }
.label-search-input::placeholder { color: hsl(215 16% 47%); }
.label-list {
  max-height: 160px; overflow-y: auto; display: flex; flex-direction: column; gap: 1px;
  border: 1px solid hsl(210 11% 86%); border-radius: 0.375rem; padding: 0.25rem;
}
.label-list-item {
  display: flex; align-items: center; gap: 0.5rem;
  padding: 0.375rem 0.5rem; border-radius: 0.375rem;
  cursor: pointer; transition: background 0.1s ease; text-align: left; width: 100%;
}
.label-list-item:hover { background: hsl(210 40% 96%); }
.label-list-item-selected { background: rgba(51, 133, 255, 0.08); }
.label-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.label-item-name { flex: 1; font-size: 0.8rem; font-weight: 500; color: hsl(222 84% 5%); }
.label-check { flex-shrink: 0; color: #3385ff; }
.label-empty { text-align: center; padding: 0.75rem; font-size: 0.75rem; color: hsl(215 16% 47%); }

/* --- Message list (DataCrazy td component) --- */
.dc-msg-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #e2e2e2 transparent;
}
.dc-msg-row {
  display: flex;
  gap: 0.25rem;
  position: relative;
}
.dc-msg-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.dc-msg-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}
.dc-msg-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border-radius: 0.375rem;
  color: hsl(206 6% 56%);
  transition: all 0.12s ease;
  cursor: pointer;
  opacity: 0;
}
.dc-msg-row:hover .dc-msg-action-btn {
  opacity: 1;
}
.dc-msg-action-btn:hover {
  color: hsl(222 84% 5%);
  background: hsl(210 40% 96%);
}
.dc-msg-action-del:hover {
  color: hsl(0 84% 60%);
  background: hsla(0, 84%, 60%, 0.06);
}
.dc-msg-drag { cursor: grab; }

/* --- Buttons inside message (DataCrazy ghost dashed) --- */
.dc-msg-btn {
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px dashed hsl(214 32% 91%);
  border-radius: 0.375rem;
  background: transparent;
  color: #3385ff;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s ease;
  text-align: left;
}
.dc-msg-btn:hover {
  border-color: #3385ff;
}
.dc-msg-btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 0.625rem;
  border-right: 1px dashed hsl(214 32% 91%);
  height: 100%;
  flex-shrink: 0;
}
.dc-msg-btn-text {
  padding: 0.25rem 0.5rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 10rem;
}
.dc-msg-btn-del {
  display: flex;
  align-items: center;
  padding: 0.25rem;
  color: hsl(206 6% 56%);
  opacity: 0;
  transition: all 0.12s ease;
}
.dc-msg-btn:hover .dc-msg-btn-del { opacity: 1; }
.dc-msg-btn-del:hover { color: hsl(0 84% 60%); }
.dc-msg-btn-add {
  justify-content: center;
  gap: 0.375rem;
  padding: 0.375rem;
  border-style: dashed;
  border-color: #3385ff;
}
.dc-msg-btn-add:hover {
  background: rgba(51, 133, 255, 0.04);
}

/* --- Settings panels --- */
.dc-msg-settings,
.dc-btn-settings {
  margin-top: 0.5rem;
}
.dc-msg-settings-inner,
.dc-btn-settings-inner {
  border: 1px solid hsl(214 32% 91%);
  border-radius: 0.5rem;
  padding: 0.75rem;
  background: white;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}

/* --- Add message type grid --- */
.chat-add-wrap {
  border-top: 1px solid hsl(214 32% 91%);
  padding-top: 0.75rem;
  margin-top: 0.5rem;
}
.chat-add-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.375rem;
}
.chat-add-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid hsl(214 32% 91%);
  font-size: 0.75rem;
  font-weight: 400;
  color: hsl(222 84% 5%);
  cursor: pointer;
  transition: all 0.12s ease;
}
.chat-add-item:hover {
  border-color: #3385ff;
  background: hsl(210 40% 96%);
}

/* --- API tabs --- */
.api-tabs {
  display: flex; gap: 0.25rem;
  border-bottom: 1px solid hsl(214 32% 91%);
  padding-bottom: 0.5rem; margin-bottom: 0.25rem; flex-wrap: wrap;
}
.api-tab {
  padding: 0.375rem 0.75rem; border-radius: 0.375rem;
  font-size: 0.75rem; font-weight: 600; color: hsl(206 6% 56%);
  background: transparent; cursor: pointer; transition: all 0.12s ease;
}
.api-tab:hover { background: hsl(210 40% 96%); color: hsl(222 84% 5%); }
.api-tab-active { background: rgba(51, 133, 255, 0.1); color: #3385ff; }
.api-tab-add {
  display: flex; align-items: center; justify-content: center;
  padding: 0.375rem; border: 1px dashed hsl(214 32% 91%);
}
.api-tab-add:hover { border-color: #3385ff; color: #3385ff; }

/* --- Day-of-week toggles (DataCrazy circular) --- */
.dc-day-toggle {
  display: flex; align-items: center; justify-content: center;
  width: 1.25rem; height: 1.25rem; border-radius: 9999px;
  font-size: 0.625rem; font-weight: 500; cursor: pointer;
  transition: all 0.15s ease; user-select: none;
}
.dc-day-active { background: #3385ff; color: white; }
.dc-day-active:hover { opacity: 0.8; }
.dc-day-inactive { border: 1px solid #3385ff; color: #3385ff; }
.dc-day-inactive:hover { background: rgba(51, 133, 255, 0.1); }
</style>
